import { useState } from "react";

import { css, keyframes } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives/responsive";
import UFOSegment from "@atlaskit/react-ufo/segment";

import { Adg4DraggableTab } from "@/components/adg4/tabs/src/draggable-tab";
import { Adg4DraggableTabList } from "@/components/adg4/tabs/src/draggable-tab-list";
import { Adg4TabPanel } from "@/components/adg4/tabs/src/tab-panel";
import { Adg4Tabs } from "@/components/adg4/tabs/src/tabs";
import { useUserContext } from "@/components/user-context/user-context-provider";
import { Picture } from "@/components/wpl/image/src/image";
import { wacToken } from "@/libraries/design/foundations";
import { Row } from "@/libraries/wpl/foundations/src/grid";
import { IncidentContentType } from "@/templates/home/home-page/src/sections-v2/jsm-xflow-hero/incident-content-type.tsx";
import { KnowledgeContentType } from "@/templates/home/home-page/src/sections-v2/jsm-xflow-hero/knowledge-content-type.tsx";
import { RequestContentType } from "@/templates/home/home-page/src/sections-v2/jsm-xflow-hero/request-content-type.tsx";
import { TrackContentType } from "@/templates/home/home-page/src/sections-v2/jsm-xflow-hero/track-content-type.tsx";
import {
    tabbedHeroSectionDatabasesMessages,
    tabbedHeroSectionPagesMessages,
    tabbedHeroSectionSpacesMessages
} from "@/templates/software/confluence/confluence-software-page/src/sections/messages/tabbed-hero-section.messages";
import { tabbedHeroSectionWhiteboardsMessages } from "@/templates/software/confluence/confluence-software-page/src/sections/messages/tabbed-hero-section.messages";

import { PagesContentType } from "../confluence-xflow-hero/content-types-section";
import { WhiteboardsContentType } from "../confluence-xflow-hero/content-types-section";
import { DatabasesContentType } from "../confluence-xflow-hero/content-types-section";
import { SpacesContentType } from "../confluence-xflow-hero/content-types-section";
import { CaptureContentType } from "../jpd-xflow-hero/capture-content-type";
import { CollaborateContentType } from "../jpd-xflow-hero/collaborate-content-type";
import { PrioritizeContentType } from "../jpd-xflow-hero/prioritize-content-type";
import { RoadmapContentType } from "../jpd-xflow-hero/roadmap-content-type";
import { LoomAIContentType } from "../loom-xflow-hero/ai-content-type.tsx";
import { MeetingsContentType } from "../loom-xflow-hero/meetings-content-type.tsx";
import { ScreenshotsContentType } from "../loom-xflow-hero/screenshots-content-type.tsx";
import { VideoContentType } from "../loom-xflow-hero/video-content-type.tsx";
import { useProduct } from "../reusable-xflow-hero-component/product-context";
import { TabList } from "../reusable-xflow-hero-component/reusable-xflow-hero-templates";
import {
    tabbedHeroSectionIntakeRequestMessages,
    tabbedHeroSectionManageIncidentsMessages,
    tabbedHeroSectionTrackChangesMessages,
    tabbedHeroSectionShareKnowledgeMessages,
    tabbedHeroSectionCaptureMessages,
    tabbedHeroSectionPrioritizeMessages,
    tabbedHeroSectionRoadmapMessages,
    tabbedHeroSectionCollaborateMessages,
    tabbedHeroSectionVideoMessages,
    tabbedHeroSectionLoomAIMessages,
    tabbedHeroSectionScreenshotsMessages,
    tabbedHeroSectionMeetingRecapMessages
} from "../reusable-xflow-hero-component/tabbed-hero-section.messages.tsx";

const iconTabListStyles = css({
    display: "flex",
    gap: "40px",
    margin: "0 auto",
    marginBlockEnd: "48px",
    [media.below.md]: {
        marginInlineStart: "32px",
        marginInlineEnd: "32px",
        marginBlockEnd: "24px",
        maxWidth: "calc(100% - 64px)",
        gap: "16px"
    }
});

const buttonResetStyle = css({
    "border": "none",
    "background": "transparent",
    "padding": "0",

    ":hover": {
        cursor: "pointer"
    }
});

const teamCardStyles = css({
    "boxSizing": "border-box",
    "display": "flex",
    "flexDirection": "column",
    "width": "173px",
    "height": "90px",
    "alignItems": "center",
    "padding": "8px",

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".hover-icon": {
        display: "none"
    },

    ":hover": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".default-icon": {
            display: "none"
        },

        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".hover-icon": {
            display: "block"
        }
    }
});

const teamCardActiveStyles = css({
    border: `1px solid ${wacToken("color.primary.black")}`,
    borderRadius: "6px",

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    span: {
        fontWeight: "700"
    }
});

const iconContainerStyles = css({
    "height": "48px",
    "width": "48px",
    "display": "flex",
    "alignItems": "center",
    "justifyContent": "center",
    [media.below.sm]: {
        height: "38px",
        width: "38px"
    },
    "@media (prefers-reduced-motion: reduce)": {
        transition: "none",
        transform: "none"
    }
});

const teamCardTextStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "24px",
    color: wacToken("color.primary.black")
});

const opacityIn = keyframes({
    "0%": {
        opacity: 0
    },
    "100%": {
        opacity: 1
    }
});

const contentContainer = css({
    "display": "flex",
    "flexDirection": "column",
    "marginInline": "auto",
    "minHeight": 460,
    [media.below.md]: {
        justifyContent: "end",
        maxWidth: "calc(100% - 32px)",
        minHeight: "auto"
    },

    [media.below.sm]: {
        maxWidth: "100%",
        minHeight: "auto"
    },

    "animation": `${opacityIn} linear 500ms 1`,

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".container-styles": {
        [media.below.md]: {
            gap: 20
        }
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".tab-text-content-description": {
        marginBlockEnd: 24,
        [media.below.sm]: {
            marginBlockEnd: 16
        }
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".tab-text-content-cta": {
        marginBlockStart: 24,
        [media.below.sm]: {
            marginBlockStart: 16,
            marginBlockEnd: 16
        }
    }
});

const TabButtonEventNameAndSubject = {
    jsm: {
        eventName: "jsm-crossflow-content-types-tab-clicked",
        actionSubject: "jsmCrossflowTab"
    },
    jpd: {
        eventName: "jpd-crossflow-content-types-tab-clicked",
        actionSubject: "jpdCrossflowTab"
    },
    loom: {
        eventName: "loom-crossflow-content-types-tab-clicked",
        actionSubject: "loomCrossflowTab"
    },
    confluence: {
        eventName: "confluence-crossflow-tab-cta-clicked",
        actionSubject: "confluenceCrossflowTab"
    }
};

export const ContentTypesSection = () => {
    const product = useProduct();
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { user } = useUserContext();
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const [hoveredTabIndex, setHoveredTabIndex] = useState<number | null>(null);

    const jsmTabList: TabList[] = [
        {
            title: intl.formatMessage(
                tabbedHeroSectionIntakeRequestMessages.tabName
            ),
            icon: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MX22.svg",
                        alt: "",
                        width: 50,
                        height: 50
                    }}
                />
            ),
            iconHover: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MX22.svg",
                        alt: "",
                        width: 50,
                        height: 50
                    }}
                />
            ),

            content: RequestContentType,
            eventProps: {
                label: "content-type-tab-intake-request",
                labelEnglish: "Intake Request"
            }
        },
        {
            title: intl.formatMessage(
                tabbedHeroSectionManageIncidentsMessages.tabName
            ),
            icon: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MWVI.svg",
                        alt: "",
                        width: 50,
                        height: 50
                    }}
                />
            ),
            iconHover: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MWVI.svg",
                        alt: "",
                        width: 50,
                        height: 50
                    }}
                />
            ),
            content: IncidentContentType,
            eventProps: {
                label: "content-type-tab-manage-incidents",
                labelEnglish: "Manage Incidents"
            }
        },
        {
            title: intl.formatMessage(
                tabbedHeroSectionTrackChangesMessages.tabName
            ),
            icon: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MWVL.svg",
                        alt: "",
                        width: 50,
                        height: 50
                    }}
                />
            ),
            iconHover: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MWVL.svg",
                        alt: "",
                        width: 50,
                        height: 50
                    }}
                />
            ),
            content: TrackContentType,
            eventProps: {
                label: "content-type-tab-track-changes",
                labelEnglish: "Track Changes"
            }
        },
        {
            title: intl.formatMessage(
                tabbedHeroSectionShareKnowledgeMessages.tabName
            ),
            icon: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT152PE.svg",
                        alt: "",
                        width: 50,
                        height: 50
                    }}
                />
            ),
            iconHover: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT152PE.svg",
                        alt: "",
                        width: 50,
                        height: 50
                    }}
                />
            ),
            content: KnowledgeContentType,
            eventProps: {
                label: "content-type-tab-share-knowledge",
                labelEnglish: "Share Knowledge"
            }
        }
    ];
    const jpdTabList: TabList[] = [
        {
            title: intl.formatMessage(tabbedHeroSectionCaptureMessages.tabName),
            icon: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MX22.svg",
                        alt: "",
                        width: 50,
                        height: 50
                    }}
                />
            ),
            iconHover: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MX22.svg",
                        alt: "",
                        width: 50,
                        height: 50
                    }}
                />
            ),
            content: CaptureContentType,
            eventProps: {
                label: "content-type-tab-capture-ideas",
                labelEnglish: "Capture"
            }
        },
        {
            title: intl.formatMessage(
                tabbedHeroSectionPrioritizeMessages.tabName
            ),
            icon: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MWXT.svg",
                        alt: "",
                        width: 50,
                        height: 50
                    }}
                />
            ),
            iconHover: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MWXT.svg",
                        alt: "",
                        width: 50,
                        height: 50
                    }}
                />
            ),
            content: PrioritizeContentType,
            eventProps: {
                label: "content-type-tab-prioritize",
                labelEnglish: "Prioritize"
            }
        },
        {
            title: intl.formatMessage(tabbedHeroSectionRoadmapMessages.tabName),
            icon: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MWWO.svg",
                        alt: "",
                        width: 50,
                        height: 50
                    }}
                />
            ),
            iconHover: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MWWO.svg",
                        alt: "",
                        width: 50,
                        height: 50
                    }}
                />
            ),
            content: RoadmapContentType,
            eventProps: {
                label: "content-type-tab-roadmap",
                labelEnglish: "Roadmap"
            }
        },
        {
            title: intl.formatMessage(
                tabbedHeroSectionCollaborateMessages.tabName
            ),
            icon: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MWW2.svg",
                        alt: "",
                        width: 50,
                        height: 50
                    }}
                />
            ),
            iconHover: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MWW2.svg",
                        alt: "",
                        width: 50,
                        height: 50
                    }}
                />
            ),
            content: CollaborateContentType,
            eventProps: {
                label: "content-type-tab-collaborate",
                labelEnglish: "Collaborate"
            }
        }
    ];
    const loomTabList: TabList[] = [
        {
            title: intl.formatMessage(tabbedHeroSectionVideoMessages.tabName),
            icon: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MWW2.svg",
                        alt: "",
                        width: 50,
                        height: 50
                    }}
                />
            ),
            iconHover: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MWW2.svg",
                        alt: "",
                        width: 50,
                        height: 50
                    }}
                />
            ),
            content: VideoContentType,
            eventProps: {
                label: "content-type-tab-video",
                labelEnglish: "Video"
            }
        },
        {
            title: intl.formatMessage(
                tabbedHeroSectionMeetingRecapMessages.tabName
            ),
            icon: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MWX5.svg",
                        alt: "",
                        width: 50,
                        height: 50
                    }}
                />
            ),
            iconHover: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MWX5.svg",
                        alt: "",
                        width: 50,
                        height: 50
                    }}
                />
            ),
            content: MeetingsContentType,
            eventProps: {
                label: "content-type-tab-meetings",
                labelEnglish: "Meetings"
            }
        },

        {
            title: intl.formatMessage(
                tabbedHeroSectionScreenshotsMessages.tabName
            ),
            icon: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT152PE.svg",
                        alt: "",
                        width: 50,
                        height: 50
                    }}
                />
            ),
            iconHover: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT152PE.svg",
                        alt: "",
                        width: 50,
                        height: 50
                    }}
                />
            ),
            content: ScreenshotsContentType,
            eventProps: {
                label: "content-type-tab-screenshots",
                labelEnglish: "Screenshots"
            }
        },
        {
            title: intl.formatMessage(tabbedHeroSectionLoomAIMessages.tabName),
            icon: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MWW8.svg",
                        alt: "",
                        width: 55,
                        height: 55
                    }}
                />
            ),
            iconHover: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MWW8.svg",
                        alt: "",
                        width: 55,
                        height: 55
                    }}
                />
            ),
            content: LoomAIContentType,
            eventProps: {
                label: "content-type-tab-loom-ai",
                labelEnglish: "Loom AI"
            }
        }
    ];

    const confluenceTabList: TabList[] = [
        {
            icon: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MX22.svg",
                        alt: "",
                        width: 55,
                        height: 55
                    }}
                />
            ),
            iconHover: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MX22.svg",
                        alt: "",
                        width: 55,
                        height: 55
                    }}
                />
            ),
            title: intl.formatMessage(tabbedHeroSectionPagesMessages.tabName),
            content: PagesContentType,
            eventProps: {
                label: intl.formatMessage(
                    tabbedHeroSectionPagesMessages.tabName
                ),
                labelEnglish:
                    tabbedHeroSectionPagesMessages.tabName.defaultMessage
            }
        },
        {
            icon: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MWX6.svg",
                        alt: "",
                        width: 55,
                        height: 55
                    }}
                />
            ),
            iconHover: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MWX6.svg",
                        alt: "",
                        width: 55,
                        height: 55
                    }}
                />
            ),
            title: intl.formatMessage(
                tabbedHeroSectionWhiteboardsMessages.tabName
            ),
            content: WhiteboardsContentType,
            eventProps: {
                label: intl.formatMessage(
                    tabbedHeroSectionWhiteboardsMessages.tabName
                ),
                labelEnglish:
                    tabbedHeroSectionWhiteboardsMessages.tabName.defaultMessage
            }
        },
        {
            icon: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MWWJ.svg",
                        alt: "",
                        width: 55,
                        height: 55
                    }}
                />
            ),
            iconHover: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MWWJ.svg",
                        alt: "",
                        width: 55,
                        height: 55
                    }}
                />
            ),
            title: intl.formatMessage(
                tabbedHeroSectionDatabasesMessages.tabName
            ),
            content: DatabasesContentType,
            eventProps: {
                label: intl.formatMessage(
                    tabbedHeroSectionDatabasesMessages.tabName
                ),
                labelEnglish:
                    tabbedHeroSectionDatabasesMessages.tabName.defaultMessage
            }
        },
        {
            icon: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MWXI.svg",
                        alt: "",
                        width: 55,
                        height: 55
                    }}
                />
            ),
            iconHover: (
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12MWXI.svg",
                        alt: "",
                        width: 55,
                        height: 55
                    }}
                />
            ),
            title: intl.formatMessage(tabbedHeroSectionSpacesMessages.tabName),
            content: SpacesContentType,
            eventProps: {
                label: intl.formatMessage(
                    tabbedHeroSectionSpacesMessages.tabName
                ),
                labelEnglish:
                    tabbedHeroSectionSpacesMessages.tabName.defaultMessage
            }
        }
    ];

    let tabList: TabList[];
    let tabButtonEventName = TabButtonEventNameAndSubject.jsm;
    if (product === "jsm") {
        tabList = jsmTabList;
        tabButtonEventName = TabButtonEventNameAndSubject.jsm;
    } else if (product === "jpd") {
        tabList = jpdTabList;
        tabButtonEventName = TabButtonEventNameAndSubject.jpd;
    } else if (product === "loom") {
        tabList = loomTabList;
        tabButtonEventName = TabButtonEventNameAndSubject.loom;
    } else if (product === "confluence") {
        tabList = confluenceTabList;
        tabButtonEventName = TabButtonEventNameAndSubject.confluence;
    } else {
        tabList = jsmTabList;
        tabButtonEventName = TabButtonEventNameAndSubject.jsm;
    }

    return (
        <UFOSegment name="homepage-xflow-contentTypesSection">
            <Row layout="12">
                <Adg4Tabs>
                    <Adg4DraggableTabList css={iconTabListStyles}>
                        {tabList.map((tab, index) => (
                            <Adg4DraggableTab
                                key={index}
                                aria-describedby={`tab-panel-${index}`}
                                aria-controls={`tab-panel-${index}`}
                                onClick={() => {
                                    setSelectedTabIndex(index);

                                    createAnalyticsEvent({
                                        eventName: tabButtonEventName.eventName,
                                        eventType: "ui",
                                        action: "clicked",
                                        actionSubject: "linkButton",
                                        actionSubjectId:
                                            tabButtonEventName.actionSubject,
                                        attributes: {
                                            reactComponent:
                                                "ContentTypesSection",
                                            tab: tab.eventProps?.labelEnglish?.toLowerCase(),
                                            userTeamType:
                                                user["extended_profile"]
                                                    ?.team_type
                                        }
                                    }).fire();
                                }}
                            >
                                <button
                                    css={buttonResetStyle}
                                    data-product-key="jsm"
                                    data-event-component="button"
                                    data-event-container="contentTypesSection"
                                    data-event="clicked"
                                    data-label-english={
                                        tab.eventProps?.labelEnglish
                                    }
                                    aria-label={tab.title}
                                    // eslint-disable-next-line @atlassian/a11y/mouse-events-have-key-events
                                    onMouseEnter={() =>
                                        setHoveredTabIndex(index)
                                    }
                                    // eslint-disable-next-line @atlassian/a11y/mouse-events-have-key-events
                                    onMouseLeave={() =>
                                        setHoveredTabIndex(null)
                                    }
                                >
                                    <div
                                        css={[
                                            teamCardStyles,
                                            selectedTabIndex === index &&
                                                teamCardActiveStyles
                                        ]}
                                    >
                                        <div css={iconContainerStyles}>
                                            {hoveredTabIndex === index &&
                                            tab.iconHover
                                                ? tab.iconHover
                                                : tab.icon}
                                        </div>
                                        <span css={teamCardTextStyles}>
                                            {tab.title}
                                        </span>
                                    </div>
                                </button>
                            </Adg4DraggableTab>
                        ))}
                    </Adg4DraggableTabList>
                    {tabList.map((tab, index) => (
                        <Adg4TabPanel
                            key={index}
                            id={`tab-panel-${index}`}
                            aria-live={
                                selectedTabIndex === index ? "polite" : "off"
                            }
                            aria-hidden={selectedTabIndex !== index}
                            aria-label={`${tab.title} content`}
                        >
                            <div css={contentContainer}>
                                {
                                    <tab.content
                                        index={index}
                                        context={product}
                                    />
                                }
                            </div>
                        </Adg4TabPanel>
                    ))}
                </Adg4Tabs>
            </Row>
        </UFOSegment>
    );
};

ContentTypesSection.displayName = "ContentTypesSection";

export default ContentTypesSection;
